import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { Booking } from '../booking';
import { HttpService } from '../service/http.service';
import { InternalBookingComment } from '../internal-booking-comment';
import { ToastMessageService } from '../toast-message.service';
import { Travel } from '../travel';
import { BookingService } from './booking.service';
import { ApiError } from '../api-error';
import { ConfirmationService } from 'primeng/api';

@Injectable({
  providedIn: 'root'
})
export class BookingSummaryService {

  travelIdLongText = "Flight number for 'AIR'\n Train number for 'TRAIN'\n Bus number for 'BUS'";

  constructor(
    private bookingService: BookingService,
    private builder: FormBuilder,
    private toastService: ToastMessageService,
    private httpService: HttpService,
    private confirmationService: ConfirmationService
  ) { }

  initTravelsForm() {
    let array = this.bookingService.bookingForm.get("travels") as FormArray;
    for (let travel of this.bookingService.booking.travels) {
      array.controls.push(this.builder.group({
        travelType: [travel.travelType, Validators.required],
        departureDate: [travel.departureDate == null ? null : moment(travel.departureDate).toDate(), Validators.required],
        arrivalDate: [travel.arrivalDate == null ? null : moment(travel.arrivalDate).toDate(), Validators.required],
        flightNumber: travel.flightNumber,
        departureAirport: travel.departureAirport,
        departureTime: travel.departureTime == null ? null : moment(travel.departureTime, "HH:mm:ss").toDate(),
        arrivalAirport: travel.arrivalAirport,
        arrivalTime: travel.arrivalTime == null ? null : moment(travel.arrivalTime, "HH:mm:ss").toDate(),
        id: travel.id,
        isDeleted: travel.isDeleted,
        transportType: travel.transportType
      }));
    }
  }

  addTravelForm() {
    let array = this.bookingService.bookingForm.get("travels") as FormArray;
    array.controls.push(this.builder.group({
      departureDate: [null, Validators.required],
      arrivalDate: [null, Validators.required],
      flightNumber: [null, Validators.required],
      departureAirport: null,
      departureTime: null,
      arrivalAirport: null,
      arrivalTime: null,
      travelType: [null, Validators.required],
      transportType: this.bookingService.transportTypes[0]
    }));
  }

  deleteTravel(form: AbstractControl, index: number) {
    let array = this.bookingService.bookingForm.get("travels") as FormArray;
    if (form.value.id) {
      this.confirmationService.confirm({
        message: "Are you sure that you want to delete this travel? index: " + index,
        accept: () => {
          this.httpService.deleteTravelById(form.value.id).subscribe(() => {
            array.removeAt(index);
          });
        }
      });
    } else {
      array.removeAt(index);
    }
  }

  checkBookingDatesConsistency() {
    if (moment(this.bookingService.bookingForm.value.startDate).isAfter(moment(this.bookingService.bookingForm.value.endDate).utc(true))) {
      this.toastService.addError("Consistency error", "Booking start date cannot be after end date");
      throw new Error("start date cannot be after end date.");
    }
  }

  checkTravelDatesConsistency() {
    let travelFormArray = this.bookingService.bookingForm.controls["travels"] as FormArray;
    for (let travelForm of travelFormArray.controls) {
      if (moment(travelForm.value.departureDate).isAfter(moment(travelForm.value.arrivalDate))) {
        this.toastService.addError("Consistency error", "Travel departure date cannot be after arrival date.");
        throw new Error("Travel dates consistency error");
      }
    }
  }

  checkSchoolAndDestinationConsistency(): Observable<boolean> {
    return this.httpService.getSchoolCountryConsistency(this.bookingService.bookingForm.value.school.id, this.bookingService.bookingForm.value.destination.id)
  }

  updateForms() {
    this.bookingService.bookingForm.get("firstName")!.setValue(this.bookingService.booking.student.firstName);
    this.bookingService.bookingForm.get("lastName")!.setValue(this.bookingService.booking.student.lastName);
    this.bookingService.bookingForm.get("startDate")!.setValue(moment(this.bookingService.booking.startDate).toDate());
    this.bookingService.bookingForm.get("endDate")!.setValue(moment(this.bookingService.booking.endDate).toDate());
    this.bookingService.bookingForm.get("school")!.setValue(this.bookingService.booking.school);
    this.bookingService.bookingForm.get("destination")!.setValue(this.bookingService.booking.destination);
    this.bookingService.bookingForm.get("languageCourse")!.setValue(this.bookingService.booking.languageCourse);
    this.bookingService.bookingForm.get("commentToSchool")!.setValue(this.bookingService.booking.commentToSchool);
    this.bookingService.bookingForm.get("targetLanguageLevel")!.setValue(this.bookingService.booking.targetLanguageLevel);

    this.bookingService.bookingForm.removeControl("travels");
    this.bookingService.bookingForm.addControl("travels", this.builder.array([]));
    this.initTravelsForm();
  }

  updateTravels() {
    let array = this.bookingService.bookingForm.get("travels") as FormArray;
    let travels: Travel[] = [];
    for (let travel of array.controls) {
      if (travel.invalid) {
        this.toastService.addError("Validation error", "travel form is not complete");
        throw new Error("Validation error in travel form");
      }
      travels.push(new Travel(
        travel.value.id,
        travel.value.travelType,
        travel.value.flightNumber,
        travel.value.departureAirport,
        moment(travel.value.departureDate).utc(true),
        travel.value.departureTime == null ? null : moment(travel.value.departureTime).format("HH:mm"),
        moment(travel.value.arrivalDate).utc(true),
        travel.value.arrivalAirport,
        travel.value.arrivalTime == null ? null : moment(travel.value.arrivalTime).format("HH:mm"),
        travel.value.isDeleted,
        travel.value.transportType
      ));
    }
    this.bookingService.booking.travels = travels;
  }

  postInternalComment() {
    this.httpService.postInternalBookingComment(new InternalBookingComment(
      this.bookingService.bookingForm.value.internalComment,
      false
    ), this.bookingService.booking.id!).subscribe(comment => {
      this.bookingService.booking.internalCommentList.unshift(comment);
      this.bookingService.bookingForm.get("internalComment")!.reset();
    });
  }

  update() {
    this.checkBookingDatesConsistency();
    this.checkTravelDatesConsistency();
    this.checkSchoolAndDestinationConsistency().subscribe(res => {
      if (res == false) {
        this.toastService.addError("Consistency Error", "Destination country does not match to selected school");
        throw new Error("School and Destination consistency error");
      }
      this.updateTravels();
      let bookingToSend = new Booking(
        moment(this.bookingService.bookingForm.value.startDate).utc(true),
        moment(this.bookingService.bookingForm.value.endDate).utc(true),
        this.bookingService.booking.isActive,
        this.bookingService.bookingForm.value.school,
        null,
        this.bookingService.bookingForm.value.destination,
        this.bookingService.bookingForm.value.languageCourse,
        this.bookingService.bookingForm.value.commentToSchool,
        this.bookingService.booking.internalCommentList,
        this.bookingService.bookingForm.value.targetLanguageLevel,
        this.bookingService.booking.accommodation,
        this.bookingService.booking.travels,
        this.bookingService.booking.student,
        this.bookingService.bookingForm.value.sentToSchool == null ? null : moment(this.bookingService.bookingForm.value.sentToSchool).utc(true),
        this.bookingService.bookingForm.value.schoolConfirmed == null ? null : moment(this.bookingService.bookingForm.value.schoolConfirmed).utc(true),
        this.bookingService.bookingForm.value.preConfirmationSent == null ? null : moment(this.bookingService.bookingForm.value.preConfirmationSent).utc(true),
        this.bookingService.bookingForm.value.insuranceSent == null ? null : moment(this.bookingService.bookingForm.value.insuranceSent).utc(true),
        this.bookingService.booking.salesAgent,
        this.bookingService.booking.quotationList,
        this.bookingService.bookingForm.value.transferType,
        this.bookingService.booking.enableAccommodationDates,
        this.bookingService.bookingForm.value.courseComment,
        this.bookingService.bookingForm.value.activitiesComment,
        this.bookingService.bookingForm.value.excursionDate == null ? null : moment(this.bookingService.bookingForm.value.excursionDate).utc(true),
        this.bookingService.bookingForm.value.promoCode,
        this.bookingService.bookingForm.value.highSchoolMeetingDate == null ? null : moment(this.bookingService.bookingForm.value.highSchoolMeetingDate).utc(true),
        this.bookingService.bookingForm.value.highSchoolTestScore,
        this.bookingService.bookingForm.value.flightRequested,
        this.bookingService.bookingForm.value.cancellationInsuranceRequested,
        this.bookingService.bookingForm.value.travelInsuranceRequested,
        this.bookingService.bookingForm.value.depositReceived == null ? null : moment(this.bookingService.bookingForm.value.depositReceived).utc(true),
        this.bookingService.bookingForm.value.completePaymentReceived == null ? null : moment(this.bookingService.bookingForm.value.completePaymentReceived).utc(true),
        this.bookingService.bookingForm.value.accommodationSent == null ? null : moment(this.bookingService.bookingForm.value.accommodationSent).utc(true),
        this.bookingService.bookingForm.value.travelInformationSent == null ? null : moment(this.bookingService.bookingForm.value.travelInformationSent).utc(true),
        this.bookingService.bookingForm.value.flightBookedDate == null ? null : moment(this.bookingService.bookingForm.value.flightBookedDate).utc(true),
        this.bookingService.bookingForm.value.visaInformationSent == null ? null : moment(this.bookingService.bookingForm.value.visaInformationSent).utc(true),
        this.bookingService.bookingForm.value.finalConfirmationSent == null ? null : moment(this.bookingService.bookingForm.value.finalConfirmationSent).utc(true),
        this.bookingService.bookingForm.value.bookingConfirmed == null ? null : moment(this.bookingService.bookingForm.value.bookingConfirmed),
        this.bookingService.bookingForm.value.travelRequestCode,
        this.bookingService.bookingForm.value.emergencyTransferPhoneNumber,
        this.bookingService.bookingForm.value.travelInsuranceRequested==false?null:this.bookingService.bookingForm.value.travelInsuranceSupplier,
        this.bookingService.bookingForm.value.cancellationInsuranceRequested==false?null:this.bookingService.bookingForm.value.cancellationInsuranceSupplier,
        this.bookingService.bookingForm.value.emergencyTransferContact,
        this.bookingService.booking.versionId,
        this.bookingService.bookingForm.value.reminderDate == null? null: moment(this.bookingService.bookingForm.value.reminderDate).utc(true),
        this.bookingService.bookingForm.value.withPartena,
        this.bookingService.booking.uuid,
        this.bookingService.booking.id
      );
      bookingToSend.cancellationInsuranceSubscriptionDate = this.bookingService.bookingForm.value.cancellationInsuranceSubscriptionDate == null?null: moment(this.bookingService.bookingForm.value.cancellationInsuranceSubscriptionDate).utc(true);
      bookingToSend.programmeType = this.bookingService.bookingForm.value.programmeType;
      if (this.bookingService.bookingForm.invalid) {
        this.toastService.addError("Booking Form Invalid", "Please complete booking form.");
        return;
      }

      if (this.bookingService.bookingForm.value.internalComment) {
        this.postInternalComment();
      }
      this.httpService.putBooking(bookingToSend)
        .subscribe(() => {
          this.toastService.addSuccess("Success", "Booking updated");
          this.bookingService.booking.student.firstName = bookingToSend.student.firstName;
          this.bookingService.booking.student.lastName = bookingToSend.student.lastName;
          this.bookingService.booking.startDate = bookingToSend.startDate;
          this.bookingService.booking.endDate = bookingToSend.endDate;
          this.bookingService.booking.student.motherLanguage = bookingToSend.student.motherLanguage;
          this.bookingService.booking.languageCourse = bookingToSend.languageCourse;
          this.bookingService.booking.school = bookingToSend.school;
          this.bookingService.booking.programmeType = bookingToSend.programmeType;
          this.httpService.getBookingById(this.bookingService.booking.id!).subscribe(booking => {
            this.bookingService.sendingUpdateRequest = false;
            this.bookingService.booking = booking;
            this.updateForms();
            this.bookingService.calculateAgeAtStartDate();
          });
        }, error => {
          this.bookingService.sendingUpdateRequest = false;
          let apiError = new ApiError(error);
          this.toastService.addError("Error", apiError.message);
        }
        );
    });
  }
}
